import { DeduplicatedSuggestionRow } from './deduplicateSuggestions';
import { getResultColumnValue, getResultName } from './resultValueHelpers';
import _ from 'lodash';

export type SortableColumnKey = 'primary' | 'secondary' | 'tertiary' | 'similarity';
export type SortDirection = 'asc' | 'desc';

export interface SuggestionsSortState {
  column: SortableColumnKey | null;
  direction: SortDirection;
}

export const INITIAL_SUGGESTIONS_SORT: SuggestionsSortState = {
  column: null,
  direction: 'asc',
};

export function hasTertiaryColumn(searchType: string, selectedFilter: string): boolean {
  if (searchType === 'DISEASES' && selectedFilter !== 'diseaseUMLSCUI') return true;
  if (searchType === 'GENES') return true;
  if (searchType === 'VARIANTS' && selectedFilter !== 'strID') return true;
  if (searchType === 'CHEMICALS' && selectedFilter !== 'CHEMBL') return true;
  return false;
}

export function getSecondaryColumnValue(searchType: string, selectedFilter: string, result: any): string {
  if (searchType === 'GENES' && selectedFilter === 'geneID') {
    return Array.isArray(result.symbol) ? result.symbol.join(', ') : result.symbol || 'Not available';
  }
  return getResultName(result, searchType, selectedFilter);
}

export function getTertiaryColumnValue(searchType: string, selectedFilter: string, result: any): string {
  if (searchType === 'DISEASES' && selectedFilter !== 'diseaseUMLSCUI' && selectedFilter !== 'name') {
    return result.diseaseUMLSCUI || 'Not available';
  }
  if (searchType === 'DISEASES' && selectedFilter === 'name') {
    return _.isNull(result.normalizedName) ? 'Not available' : result.normalizedName;
  }
  if (searchType === 'GENES') {
    if (selectedFilter === 'symbol' || selectedFilter === 'geneID') {
      return result.ensembl_ids || 'Not available';
    }
    if (selectedFilter === 'name') {
      return result.normalizedName || 'Not available';
    }
    return result.symbol || 'Not available';
  }
  if (searchType === 'VARIANTS' && selectedFilter !== 'strID') {
    return result.strID || 'Not available';
  }
  if (searchType === 'CHEMICALS' && selectedFilter !== 'CHEMBL' && selectedFilter !== 'name') {
    return result.chemID || 'Not available';
  }
  if (searchType === 'CHEMICALS' && selectedFilter === 'name') {
    return result.normalizedName || 'Not available';
  }
  return 'Not available';
}

function normalizeSortText(value: unknown): string {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text || text === 'Not available') return '';
  return text.toLocaleLowerCase();
}

export function getRowSortValue(
  row: DeduplicatedSuggestionRow,
  column: SortableColumnKey,
  searchType: string,
  selectedFilter: string,
): string | number {
  if (row.type !== 'result') return '';

  const { data, query } = row;

  if (column === 'similarity') {
    const numeric = Number(data?.similarity);
    return Number.isFinite(numeric) ? numeric : Number.NEGATIVE_INFINITY;
  }

  if (column === 'primary') {
    return normalizeSortText(getResultColumnValue(searchType, selectedFilter, data, query));
  }

  if (column === 'secondary') {
    return normalizeSortText(getSecondaryColumnValue(searchType, selectedFilter, data));
  }

  return normalizeSortText(getTertiaryColumnValue(searchType, selectedFilter, data));
}

function compareSortValues(a: string | number, b: string | number, direction: SortDirection): number {
  const emptyA = a === '' || a === Number.NEGATIVE_INFINITY;
  const emptyB = b === '' || b === Number.NEGATIVE_INFINITY;

  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  let result = 0;
  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
  }

  return direction === 'asc' ? result : -result;
}

/** Sorts matched rows; error / not-found rows stay at the end. Renumbers `#`. */
export function sortSuggestionRows(
  rows: DeduplicatedSuggestionRow[],
  sortState: SuggestionsSortState,
  searchType: string,
  selectedFilter: string,
): DeduplicatedSuggestionRow[] {
  if (!sortState.column) {
    return rows.map((row, index) => ({ ...row, rowNum: index + 1 }));
  }

  const matched = rows.filter(row => row.type === 'result');
  const unmatched = rows.filter(row => row.type !== 'result');

  const sortedMatched = [...matched].sort((left, right) =>
    compareSortValues(
      getRowSortValue(left, sortState.column!, searchType, selectedFilter),
      getRowSortValue(right, sortState.column!, searchType, selectedFilter),
      sortState.direction,
    ),
  );

  return [...sortedMatched, ...unmatched].map((row, index) => ({ ...row, rowNum: index + 1 }));
}

export function nextSuggestionsSortState(
  current: SuggestionsSortState,
  column: SortableColumnKey,
): SuggestionsSortState {
  if (current.column !== column) {
    return { column, direction: 'asc' };
  }
  if (current.direction === 'asc') {
    return { column, direction: 'desc' };
  }
  return INITIAL_SUGGESTIONS_SORT;
}
